/**
 * #440 — keep the branding-removal fee paired with its design.
 *
 * The fee line renders without a quantity stepper or a remove button, but the
 * DESIGN line it pays for keeps both. So the customer can remove the keyfobs
 * and leave an orphaned fee behind — they would be charged for removing
 * branding from a design they are no longer buying.
 *
 * This sweeps orphans: a line carrying `_branding_removal_for` whose target
 * batch no longer has a design line in the cart is removed. Also sweeps the
 * propertyless ghost (#554): the branding-removal product with no pointer,
 * which the property-based check alone cannot see.
 *
 * ## What this is NOT
 *
 * Not enforcement. `/cart/change.js` is reachable from devtools, so a customer
 * can delete the fee and keep the flagged design line. That is caught properly
 * in two places that the browser cannot reach: the Cart & Checkout Validation
 * function, and — for draft, phone, wholesale and API-created orders that skip
 * validation entirely — the `orders/paid` webhook, which grants the entitlement
 * only against a real, positively-priced Shopify line. This file exists so the
 * ordinary case behaves sensibly, not so the money is safe.
 */
(function () {
  'use strict';

  var FEE_PROPERTY = '_branding_removal_for';
  var FEE_HANDLE = 'branding-removal';
  var sweeping = false;

  function propertiesOf(item) {
    return (item && item.properties) || {};
  }

  function isBrandingFeeItem(item) {
    if (!item) return false;
    if (propertiesOf(item)[FEE_PROPERTY]) return true;
    // Propertyless ghost: identify by product handle from /cart.js.
    return item.handle === FEE_HANDLE;
  }

  /** Line keys of fee lines whose design line is gone (or never existed). */
  function findOrphanFeeKeys(items) {
    var liveBatchIds = {};
    items.forEach(function (item) {
      var batchId = propertiesOf(item)['_batch_id'];
      if (batchId) liveBatchIds[batchId] = true;
    });

    return items
      .filter(function (item) {
        if (!isBrandingFeeItem(item)) return false;
        var target = propertiesOf(item)[FEE_PROPERTY];
        // Ghost (no pointer) or unpaired pointer → remove.
        return !target || !liveBatchIds[target];
      })
      .map(function (item) {
        return item.key;
      });
  }

  async function sweep() {
    if (sweeping) return;
    sweeping = true;
    try {
      var cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) return;
      var cart = await cartResponse.json();
      var orphans = findOrphanFeeKeys(cart.items || []);
      if (orphans.length === 0) return;

      // One at a time: /cart/change.js takes a single line, and each change
      // re-keys nothing but does return the updated cart.
      for (var i = 0; i < orphans.length; i++) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: orphans[i], quantity: 0 })
        });
      }

      // Do NOT reload. A reload re-reads the SSR live_cart metafield, which
      // still holds the orphan until sync completes — that was the 2–5s
      // appear/disappear loop (#554). Sync the cleaned cart, then refresh
      // cart sections in place.
      document.dispatchEvent(new CustomEvent('nfckey:cart-mutated'));
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { data: {} } }));
    } catch (err) {
      // Never break the cart page over this — the webhook is the real gate.
      console.error('[BrandingFeePairing] sweep failed:', err);
    } finally {
      sweeping = false;
    }
  }

  document.addEventListener('DOMContentLoaded', sweep);
  // `cart:update`, NOT `cart:updated` — the name is defined once by the theme
  // itself, as `ThemeEvents.cartUpdate` in `assets/events.js`, and dispatched
  // by `component-cart-items.js` after every AJAX line change. The guessed
  // name meant removing a design line left its fee behind until the customer
  // happened to reload the page, which is exactly the orphan this file exists
  // to prevent (#553). CartUpdateEvent bubbles, so listening on document
  // catches it from the cart page and the cart drawer alike.
  document.addEventListener('cart:update', sweep);
})();
